export interface ServiceArea {
  name: string;
  county: string;
  icon?: string;
}

interface Props {
  areas: ServiceArea[];
  localText?: string;
  mapSrc?: string;
  title?: string;
  subtitle?: string;
}

const defaultMapSrc = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d27489.6!2d-96.3974!3d30.1669!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8644919b00000001%3A0x1!2sBrenham%2C+TX+77833!5e0!3m2!1sen!2sus!4v1700000000010!5m2!1sen!2sus";

export default function SectionServiceAreas({ areas, localText, mapSrc, title = 'Serving Clients Across Texas', subtitle }: Props) {
  return (
    <section className="bg-white py-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #000 0, #000 1px, transparent 0, transparent 40px), repeating-linear-gradient(90deg, #000 0, #000 1px, transparent 0, transparent 40px)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-[11px] font-bold uppercase tracking-[0.28em]">Service Areas</span>
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
          </div>
          <h2
            className="font-bold text-[#0c1520] leading-tight mb-5"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(30px, 4vw, 52px)' }}
          >
            {title}
          </h2>
          <div className="flex items-center justify-center gap-2.5 mb-5">
            <div className="w-10 h-[3px] bg-[#c9a84c] rounded-full" />
            <div className="w-3 h-[3px] bg-[#c9a84c]/35 rounded-full" />
          </div>
          {subtitle && <p className="text-[#64748b] text-lg max-w-2xl mx-auto leading-relaxed">{subtitle}</p>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Left — cities grid + local text */}
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
              {areas.map((area) => (
                <div
                  key={area.name}
                  className="group flex items-center gap-3 p-4 bg-[#f9f8f4] border border-[#ece9de] rounded-xl hover:border-[#c9a84c]/50 hover:bg-[#fef9ef] hover:-translate-y-0.5 transition-all duration-250 cursor-default"
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-[#050a12] group-hover:bg-[#c9a84c] rounded-lg flex-shrink-0 transition-colors duration-300">
                    <i className={`${area.icon ?? 'ri-map-pin-line'} text-[#c9a84c] group-hover:text-[#050a12] text-sm transition-colors duration-300`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[#0c1520] font-bold text-sm truncate">{area.name}</p>
                    <p className="text-[#94a3b8] text-xs truncate">{area.county}</p>
                  </div>
                </div>
              ))}
            </div>

            {localText && (
              <div className="relative bg-[#050a12] rounded-2xl p-6 overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#c9a84c] to-[#c9a84c]/20 rounded-l-2xl" />
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#c9a84c]/15 border border-[#c9a84c]/20 rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-2-fill text-[#c9a84c] text-sm" />
                  </div>
                  <p className="text-white/65 text-sm leading-relaxed">{localText}</p>
                </div>
              </div>
            )}
          </div>

          {/* Right — Map */}
          <div className="relative group">
            {/* Gold glow border */}
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#c9a84c]/15 to-transparent opacity-50 group-hover:opacity-80 transition-opacity duration-500" />
            <div className="relative rounded-2xl overflow-hidden border border-[#ece9de] group-hover:border-[#c9a84c]/30 transition-colors duration-500" style={{ height: '440px' }}>
              <iframe
                title="Service Area Map"
                src={mapSrc ?? defaultMapSrc}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            {/* Map attribution overlay */}
            <div className="absolute bottom-3 left-3 bg-[#050a12]/90 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-2 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-2-fill text-[#c9a84c] text-xs" />
              </div>
              <span className="text-white/70 text-xs font-semibold">105 W. Alamo St, Brenham TX</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
